import { useEffect, useRef, useState } from 'react';
import type { HeroBackground } from '../types';

// Darkening overlay that keeps hero copy legible over any non-image background.
// Image backgrounds use CSS background-image directly and don't need this.
const OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  background:
    'linear-gradient(160deg, rgba(10,10,40,0.82) 0%, rgba(20,20,58,0.78) 50%, rgba(10,10,30,0.88) 100%)',
};

const BG_LAYER: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
  pointerEvents: 'none',
};

// ── Gradient variant ──────────────────────────────────────────────────────────
// Pure CSS blob-overlay gradient — no assets, reads brand CSS tokens at render time.
function GradientBackground() {
  return (
    <>
      <div
        style={{
          ...BG_LAYER,
          background:
            'linear-gradient(155deg, var(--evt-dark, #0A0A2E) 0%, #0F0F3C 55%, var(--evt-dark-3, #1E1E52) 100%)',
        }}
      />
      <div
        style={{
          ...BG_LAYER,
          background:
            'radial-gradient(ellipse 420px 420px at -60px -80px, rgba(var(--evt-primary-rgb, 255,107,53),0.22), transparent 65%)',
        }}
      />
      <div
        style={{
          ...BG_LAYER,
          background:
            'radial-gradient(ellipse 380px 380px at calc(10% + 380px) calc(100% + 60px), rgba(var(--evt-accent-rgb, 245,195,0),0.12), transparent 65%)',
        }}
      />
      <div
        style={{
          ...BG_LAYER,
          background:
            'radial-gradient(ellipse 360px 360px at 45% 40%, rgba(90,100,220,0.18), transparent 65%)',
        }}
      />
      <div style={OVERLAY_STYLE} aria-hidden="true" />
    </>
  );
}

// ── Video variant ─────────────────────────────────────────────────────────────
function VideoBackground({ videoUrl, posterUrl }: { videoUrl: string; posterUrl?: string }) {
  const [allowMotion, setAllowMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setAllowMotion(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return (
    <>
      {allowMotion && (
        <video
          style={{ ...BG_LAYER, objectFit: 'cover' }}
          poster={posterUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      )}
      <div style={OVERLAY_STYLE} aria-hidden="true" />
    </>
  );
}

// ── Animated canvas variant ("network" preset) ────────────────────────────────
// Reads brand colors from CSS custom properties so it adapts to any event brand.
// Falls back to a static frame for prefers-reduced-motion visitors.
function AnimatedBackground({
  density = 46,
  speed = 1,
}: {
  density?: number;
  speed?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;
    let rafId = 0;

    // Read brand tokens from the DOM so colors adapt to the active brand/event.
    // Falls back to TSB navy + coral literals if tokens aren't injected yet.
    function getBrandColors() {
      const style = getComputedStyle(document.documentElement);
      const primary = style.getPropertyValue('--evt-primary').trim() || '#FF6B35';
      const dark = style.getPropertyValue('--evt-dark').trim() || '#0A0A2E';
      const dark2 = style.getPropertyValue('--evt-dark-2').trim() || '#14143C';
      return { primary, dark, dark2 };
    }

    function resize() {
      const r = container!.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas!.width = W * dpr;
      canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (reduceMotion) drawFrame(0);
    }

    // Particles — count driven by density prop
    const N = Math.max(10, density);
    type Particle = { x: number; y: number; vx: number; vy: number; r: number; glow: boolean };
    const pts: Particle[] = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 2e-4 * speed,
      vy: (Math.random() - 0.5) * 2e-4 * speed,
      r: 1 + Math.random() * 2.2,
      glow: Math.random() < 0.18,
    }));

    function drawFrame(t: number) {
      if (!ctx || W === 0 || H === 0) return;
      const { dark, dark2 } = getBrandColors();

      // Background gradient
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, dark);
      g.addColorStop(0.5, dark2);
      g.addColorStop(1, dark);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      // Coral glow orb (drifts slowly)
      const gx = W * (0.3 + 0.18 * Math.sin(t * 0.6));
      const gy = H * (0.35 + 0.12 * Math.cos(t * 0.5));
      const rg1 = ctx.createRadialGradient(gx, gy, 0, gx, gy, W * 0.42);
      rg1.addColorStop(0, 'rgba(255,107,53,0.16)');
      rg1.addColorStop(1, 'rgba(255,107,53,0)');
      ctx.fillStyle = rg1;
      ctx.fillRect(0, 0, W, H);

      // Blue/purple glow orb
      const bx = W * (0.72 + 0.14 * Math.cos(t * 0.4));
      const by = H * (0.6 + 0.15 * Math.sin(t * 0.7));
      const rg2 = ctx.createRadialGradient(bx, by, 0, bx, by, W * 0.4);
      rg2.addColorStop(0, 'rgba(90,110,220,0.14)');
      rg2.addColorStop(1, 'rgba(90,110,220,0)');
      ctx.fillStyle = rg2;
      ctx.fillRect(0, 0, W, H);

      // Move particles (skip when reduced-motion)
      if (!reduceMotion) {
        for (const p of pts) {
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > 1) p.vx *= -1;
          if (p.y < 0 || p.y > 1) p.vy *= -1;
        }
      }

      // Connection lines
      ctx.lineWidth = 1;
      for (let a = 0; a < N; a++) {
        for (let b = a + 1; b < N; b++) {
          const pa = pts[a], pb = pts[b];
          const dx = (pa.x - pb.x) * W;
          const dy = (pa.y - pb.y) * H;
          const d = Math.hypot(dx, dy);
          if (d < 150) {
            ctx.strokeStyle = `rgba(255,255,255,${(1 - d / 150) * 0.1})`;
            ctx.beginPath();
            ctx.moveTo(pa.x * W, pa.y * H);
            ctx.lineTo(pb.x * W, pb.y * H);
            ctx.stroke();
          }
        }
      }

      // Nodes + glow halos
      for (const pt of pts) {
        const x = pt.x * W, y = pt.y * H;
        if (pt.glow) {
          const pr = ctx.createRadialGradient(x, y, 0, x, y, 14);
          pr.addColorStop(0, 'rgba(255,107,53,0.6)');
          pr.addColorStop(1, 'rgba(255,107,53,0)');
          ctx.fillStyle = pr;
          ctx.beginPath();
          ctx.arc(x, y, 14, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = pt.glow ? 'rgba(245,160,110,0.95)' : 'rgba(255,255,255,0.5)';
        ctx.beginPath();
        ctx.arc(x, y, pt.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Vignette
      const vg = ctx.createRadialGradient(W / 2, H / 2, H * 0.2, W / 2, H / 2, W * 0.55);
      vg.addColorStop(0, 'rgba(42,42,82,0)');
      vg.addColorStop(1, 'rgba(20,20,42,0.55)');
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
    }

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    resize();

    if (reduceMotion) return () => window.removeEventListener('resize', handleResize);

    const t0 = performance.now();
    function loop(now: number) {
      drawFrame(((now - t0) * 0.006) / 16.67);
      rafId = requestAnimationFrame(loop);
    }
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [density, speed]);

  return (
    <div ref={containerRef} style={{ ...BG_LAYER, overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%' }}
        aria-hidden="true"
      />
      <div style={OVERLAY_STYLE} aria-hidden="true" />
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────
// Drop inside any `position: relative; overflow: hidden` hero shell.
// Hero content sits at z-index 2+; this component occupies z-index 0–1.
export function HeroBackgroundLayer({ bg }: { bg: HeroBackground }) {
  switch (bg.type) {
    case 'gradient':
      return <GradientBackground />;
    case 'video':
      return <VideoBackground videoUrl={bg.videoUrl} posterUrl={bg.posterUrl} />;
    case 'animated':
      return <AnimatedBackground density={bg.density} speed={bg.speed} />;
    case 'image':
      // Image backgrounds are applied as CSS background-image on the hero shell itself
      // (same as the old backgroundUrl pattern) — no extra layer needed.
      return null;
  }
}
